import type { BatchPutawayRowValues } from './batchPutaway';

export type CryovialValues = Omit<
  BatchPutawayRowValues<string>,
  'positionId' | 'barcode'
> & { notes?: string };
export type CryovialField = keyof CryovialValues;
export type CryovialDraft = {
  positionId: string;
  barcode: string;
  overrides: CryovialValues;
};
export const cryovialFields: CryovialField[] = [
  'cellLineId',
  'cellMaterialId',
  'label',
  'passage',
  'cellCount',
  'ownerName',
  'freezeDate',
  'expiryDate',
  'notes',
];
export const hasOverride = (row: CryovialDraft, field: CryovialField) =>
  Object.prototype.hasOwnProperty.call(row.overrides, field);
export function resolveCryovial(common: CryovialValues, row: CryovialDraft) {
  return {
    ...common,
    ...row.overrides,
    positionId: row.positionId,
    barcode: row.barcode.trim(),
  };
}
export function updateCryovial(
  common: CryovialValues,
  row: CryovialDraft,
  field: CryovialField,
  value: string,
  defaultMaterial: (line: string) => string,
): CryovialDraft {
  const overrides = { ...row.overrides, [field]: value };
  // A line and its material are one dependent group: neither can inherit from
  // a different line after a subsequent common-value change.
  if (field === 'cellLineId') overrides.cellMaterialId = defaultMaterial(value);
  if (field === 'cellMaterialId')
    overrides.cellLineId = resolveCryovial(common, row).cellLineId ?? '';
  return { ...row, overrides };
}
export function resetCryovial(
  row: CryovialDraft,
  field: CryovialField,
): CryovialDraft {
  const overrides = { ...row.overrides };
  delete overrides[field];
  if (field === 'cellLineId' || field === 'cellMaterialId') {
    delete overrides.cellLineId;
    delete overrides.cellMaterialId;
  }
  return { ...row, overrides };
}
export type CryovialIssue = {
  positionId: string;
  field: CryovialField | 'barcode';
  code: 'required' | 'duplicate' | 'expiry' | 'material';
};
export function validateCryovials(
  common: CryovialValues,
  rows: CryovialDraft[],
  materials: { id: string; cellLineId: string }[],
): CryovialIssue[] {
  const issues: CryovialIssue[] = [];
  const resolved = rows.map((row) => resolveCryovial(common, row));
  for (const row of resolved) {
    for (const field of ['cellLineId', 'freezeDate', 'ownerName'] as const) {
      if (!row[field]?.trim())
        issues.push({ positionId: row.positionId, field, code: 'required' });
    }
    if (
      row.cellMaterialId &&
      !materials.some(
        (material) =>
          material.id === row.cellMaterialId &&
          material.cellLineId === row.cellLineId,
      )
    )
      issues.push({
        positionId: row.positionId,
        field: 'cellMaterialId',
        code: 'material',
      });
    if (row.expiryDate && row.freezeDate && row.expiryDate < row.freezeDate)
      issues.push({
        positionId: row.positionId,
        field: 'expiryDate',
        code: 'expiry',
      });
    if (
      row.barcode &&
      resolved.filter((other) => other.barcode === row.barcode).length > 1
    )
      issues.push({
        positionId: row.positionId,
        field: 'barcode',
        code: 'duplicate',
      });
  }
  return issues;
}
